import { AsyncPipe } from "@angular/common";
import { Component, ElementRef, HostListener, inject, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { Menu } from "./menu/menu";
import { UserInfo, UserInfoData } from "./user-info/user-info";
import { MatIcon } from "@angular/material/icon";
import { MatDialog } from "@angular/material/dialog";
import { UmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger";
import { ForsyningIndstillinger } from "@e-forsyning/common/forsyning-indstillinger";
import { Bruger } from "@e-forsyning/common/bruger";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { ThemeMode, ThemeService, SvgIconRegistry } from "@dffedb/util";
import { TeknikerPasswordCapabilityService } from "@e-forsyning/common/auth";
import { mdiCheck, mdiChevronDown, mdiChevronRight, mdiPalette, mdiMessageProcessingOutline } from "@mdi/js";
import { FeedbackDialogComponent } from "./feedback-dialog/feedback-dialog.component";
import { webVersion } from "./user-info/web-version";
import { Capacitor } from "@capacitor/core";

@Component({
    selector: "top-navigation",
    imports: [AsyncPipe, Menu, UserInfo, MatIcon, RouterLink, RouterLinkActive],
    templateUrl: "./top-navigation.html",
    styleUrl: "./top-navigation.scss"
})
export class TopNavigation implements OnChanges {
    @Input()
    public indstillinger: UmbracoIndstillinger;

    @Input()
    public aktuelBruger: Bruger | null = null;

    @Input()
    public aktuelForsyning: ForsyningIndstillinger | null = null;

    @Input()
    public menuItems: { label: string; icon: string; route: string; id?: string }[] = [];

    @ViewChild("burgerButton") private readonly burgerButton!: ElementRef<HTMLButtonElement>;

    public showMenu: boolean = false;
    public showThemeSubmenu: boolean = false;
    public userInfoData: UserInfoData | null = null;
    private readonly themeService = inject(ThemeService);
    private readonly router = inject(Router);
    public readonly themeOptions = this.themeService.getThemeOptions();
    public readonly activeTheme = this.themeService.activeTheme;
    public readonly teknikerPasswordFeaturesSupported$ = inject(TeknikerPasswordCapabilityService).supportsTeknikerPasswordFeatures$();
    private readonly dialog = inject(MatDialog);

    public readonly webVersion = webVersion;
    public readonly klientLabel = Capacitor.isNativePlatform() ? "app" : "browser";

    constructor(
        private elementRef: ElementRef,
        svgIconRegistry: SvgIconRegistry
    ) {
        svgIconRegistry.registerIcon(mdiPalette, "menu-theme");
        svgIconRegistry.registerIcon(mdiChevronRight, "chevron-right");
        svgIconRegistry.registerIcon(mdiChevronDown, "chevron-down");
        svgIconRegistry.registerIcon(mdiCheck, "check");
        svgIconRegistry.registerIcon(mdiMessageProcessingOutline, "feedback");
    }

    @HostListener("document:click", ["$event"])
    public onDocumentClick(event: MouseEvent): void {
        if (this.showMenu && !this.elementRef.nativeElement.contains(event.target)) {
            this.showMenu = false;
            this.showThemeSubmenu = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes["aktuelBruger"] && !changes["aktuelForsyning"] && !changes["indstillinger"]) {
            return;
        }

        const initialer = (this.aktuelBruger?.initialer || "").trim();
        this.userInfoData = {
            user: {
                initialer,
                name: this.aktuelBruger?.navn ?? "",
                email: this.aktuelBruger?.email ?? "",
                phone: ""
            },
            works: {
                name: this.aktuelForsyning?.forsyningNavn ?? "",
                phone: this.indstillinger?.forsyningWeb?.firmaInfo?.telefon ?? "",
                address: ""
            }
        };
    }

    @HostListener("document:keydown.escape")
    public onEscape(): void {
        if (this.showMenu) {
            this.closeAndFocus();
        }
    }

    public toggleMenu(): void {
        this.showMenu = !this.showMenu;
        if (!this.showMenu) {
            this.showThemeSubmenu = false;
        }
        if (this.showMenu) {
            setTimeout(() => {
                const firstItem = document.querySelector<HTMLElement>('#user-dropdown-menu [role="menuitem"]');
                firstItem?.focus();
            });
        }
    }

    public closeAndFocus(): void {
        this.showMenu = false;
        this.showThemeSubmenu = false;
        setTimeout(() => this.burgerButton?.nativeElement?.focus());
    }

    public toggleThemeSubmenu(): void {
        this.showThemeSubmenu = !this.showThemeSubmenu;
    }

    public selectTheme(mode: ThemeMode): void {
        this.themeService.setTheme(mode, true);
    }

    public onSkiftAdgangskode(): void {
        this.showMenu = false;
        this.showThemeSubmenu = false;
        void this.router.navigateByUrl("/skift-kodeord");
    }

    public openFeedback(): void {
        this.showMenu = false;
        this.showThemeSubmenu = false;
        this.dialog.open(FeedbackDialogComponent, {
            width: "100%",
            maxWidth: "768px",
            data: {
                aktuelBruger: this.aktuelBruger,
                indstillinger: this.indstillinger
            }
        });
    }
}
